import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { cancelTokenSelector } from '../redux/selectors/selectors';
import { setAlarm } from '../redux/actions/general';
import { BLOCK_LIST_PROP_NAME_MAP, IP_BLOCKLIST } from '../constants/general';
import { GUARDIAN_API_URL } from '../constants/config';
import * as browserStorage from '../misc/BrowserStorageManager';
import { getDomainFromURL } from '../util/UtilFunctions';
import { getUnifiedErrorObject } from '../util/ErrorUnifier';
import { useMutation } from '@tanstack/react-query';

export function getBlockErrorMessage(error, fallback) {
   if (!error) return '';
   if (error.errorBody?.errorCode) return `${error.errorBody.message} (Error code: ${error.errorBody.errorCode})`;
   return fallback;
}

function useBlockAction({ valueToBeBlocked, type }) {
   const streamGuardToken = browserStorage.getJwtToken();
   const blockedValue = type === IP_BLOCKLIST ? valueToBeBlocked : getDomainFromURL(valueToBeBlocked);

   const dispatch = useDispatch();
   const cancelToken = useSelector(cancelTokenSelector);

   const httpHeader = useMemo(() => ({ 'X-BINTU-TOKEN': streamGuardToken, 'Content-Type': 'application/json' }), [
      streamGuardToken,
   ]);

   const url = `${GUARDIAN_API_URL}/${type}`;

   const { mutate, isLoading, isSuccess, error: mutationError, reset } = useMutation(
      ({ method, body }) =>
         axios({
            method,
            url,
            data: body,
            headers: httpHeader,
            cancelToken: cancelToken.token,
            withCredentials: false,
         }).then((res) => res.data),
      {
         onError: (err) => {
            if (!(err instanceof axios.Cancel)) {
               const errorObj = getUnifiedErrorObject(err);
               dispatch(setAlarm('danger', { message: getBlockErrorMessage(errorObj, err.message) }));
            }
         },
      },
   );

   const block = useCallback(
      (tag) => {
         const body = { [BLOCK_LIST_PROP_NAME_MAP[type]]: blockedValue, type: 'deny' };
         if (tag) body.tag = tag;
         mutate({ method: 'POST', body });
      },
      [type, blockedValue, mutate],
   );

   const unblock = useCallback(() => {
      mutate({ method: 'DELETE', body: { [BLOCK_LIST_PROP_NAME_MAP[type]]: blockedValue, type: 'deny' } });
   }, [type, blockedValue, mutate]);

   return {
      block,
      unblock,
      isLoading,
      isSuccess,
      error: mutationError ? getUnifiedErrorObject(mutationError) : undefined,
      reset,
   };
}

export default useBlockAction;
